import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../components/atlas/helpers";
import { useAtlas } from "../store/atlasStore";
import { RefreshCw, Play, BarChart3, Eye, Trash2, Cpu, CheckCircle } from "lucide-react";

function fmt(v, d = 3) {
  if (v == null || !isFinite(+v)) return "—";
  return Number(v).toFixed(d);
}

function pct(v) {
  if (v == null) return "—";
  const n = Number(v);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
}

export function ModelsWorkspace() {
  const { state } = useAtlas();
  const { provider, symbol, interval } = state;

  const [models, setModels] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Inference state
  const [inferResult, setInferResult] = useState(null);
  const [inferLoading, setInferLoading] = useState(false);

  // Backtest state
  const [btResult, setBtResult] = useState(null);
  const [btLoading, setBtLoading] = useState(false);

  const fetchModels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/learning/models`, { 
        params: { limit: 100, provider, symbol } 
      });
      const modelsSorted = r.data.sort((a, b) => {
        const retA = a.latest_backtest?.total_return_pct || -9999;
        const retB = b.latest_backtest?.total_return_pct || -9999;
        return retB - retA;
      });
      setModels(modelsSorted);
      setSelected(curr => curr || modelsSorted[0] || null);
    } catch (e) {
      setError(e.message || JSON.stringify(e));
    }
    setLoading(false);
  }, [provider, symbol]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const handleDelete = async (e, m) => {
    e.stopPropagation();
    if (!window.confirm(`Delete model ${m.model_id}?`)) return;
    try {
      await axios.delete(`${API}/learning/models/${m.model_id}`);
      if (selected?.model_id === m.model_id) setSelected(null);
      fetchModels();
    } catch (err) {
      alert("Failed to delete model: " + (err.response?.data?.detail || err.message));
    }
  };

  const runInference = async (mId) => {
    setInferLoading(true);
    setInferResult(null);
    try {
      const r = await axios.post(`${API}/learning/inference/predict`, {
        model_id: mId, provider, symbol, interval, period: "3y"
      });
      setInferResult(r.data);
    } catch (e) {
      setInferResult({ error: e.message });
    }
    setInferLoading(false);
  };

  const runBacktest = async (mId) => {
    setBtLoading(true);
    setBtResult(null);
    try {
      const r = await axios.post(`${API}/learning/backtest/run`, {
        model_id: mId, provider, symbol, interval, period: "3y",
        warmup_bars: 150, initial_cash: 100000, position_size_pct: 20.0,
        stop_loss_pct: 2.0, take_profit_pct: 4.0
      }, { timeout: 300_000 });
      setBtResult(r.data);
    } catch (e) {
      setBtResult({ error: e.message });
    }
    setBtLoading(false);
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", height: "100%", gap: 4, padding: 4 }}>
      {/* ── LEFT Column: Models List ───────────────────────────────────── */}
      <div className="panel" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div className="panel-header">
          <span className="panel-title">Model Registry</span>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {error && <span className="badge badge-red" style={{ fontSize: 9 }}>Error: {error}</span>}
            <span className="badge">{models.length} stored</span>
            <button className="btn btn-ghost btn-icon" onClick={fetchModels} disabled={loading}>
              <RefreshCw size={11} className={loading ? "spin" : ""} />
            </button>
          </div>
        </div>
        <div className="table-container" style={{ flex: 1 }}>
          <table className="dt">
            <thead>
              <tr>
                <th>Model ID</th>
                <th>Kind</th>
                <th>Experiment ID</th>
                <th className="r">Features</th>
                <th className="r">Return (BT)</th>
                <th className="r">Sharpe (BT)</th>
                <th className="r">Hit Rate (BT)</th>
                <th className="r">Accuracy</th>
                <th className="r">IC</th>
                <th>Created</th>
                <th style={{ width: 30 }}></th>
              </tr>
            </thead>
            <tbody>
              {models.map(m => (
                <tr
                  key={m.model_id}
                  style={{ cursor: "pointer" }}
                  className={selected?.model_id === m.model_id ? "selected" : ""}
                  onClick={() => {
                    setSelected(m);
                    setInferResult(null);
                    setBtResult(null);
                  }}
                >
                  <td><span className="mono text-blue" style={{ fontSize: 10 }}>{m.model_id?.slice(0,12)}</span></td>
                  <td><span className="badge badge-violet">{m.kind}</span></td>
                  <td><span className="mono text-dim" style={{ fontSize: 9 }}>{m.experiment_id?.slice(0,8)}</span></td>
                  <td className="r">{m.feature_ids?.length || 0}</td>
                  <td className="r font-medium" style={{ color: (m.latest_backtest?.total_return_pct ?? 0) > 0 ? "var(--green)" : "var(--red)" }}>
                    {fmt(m.latest_backtest?.total_return_pct, 2)}%
                  </td>
                  <td className="r">{fmt(m.latest_backtest?.sharpe, 2)}</td>
                  <td className="r">{m.latest_backtest?.hit_rate != null ? fmt(m.latest_backtest.hit_rate * 100, 1) + '%' : '-'}</td>
                  <td className="r">{fmt(m.metrics?.accuracy_mean)}</td>
                  <td className="r" style={{ color: (m.metrics?.information_coefficient_mean ?? 0) > 0 ? "var(--green)" : "var(--red)" }}>
                    {fmt(m.metrics?.information_coefficient_mean, 4)}
                  </td>
                  <td className="label-cell" style={{ fontSize: 10 }}>{(m.created_at || "").slice(0,10)}</td>
                  <td>
                    <button className="icon-btn" style={{ color: "var(--red)" }} onClick={(e) => handleDelete(e, m)}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {!models.length && (
                <tr><td colSpan={10} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: 24 }}>No models registered in system</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── RIGHT Column: Model Detail Panel ───────────────────────────── */}
      <div className="panel" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
        <div className="panel-header">
          <span className="panel-title">Model Profile</span>
          {selected && <span className="badge badge-violet">{selected.kind}</span>}
        </div>
        <div className="panel-body" style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
          {selected ? (
            <>
              {/* Identity & Origin */}
              <div>
                <div className="section-sep"><span className="s-label">Identity & Origin</span></div>
                {[
                  ["Model ID", selected.model_id],
                  ["Experiment", selected.experiment_id],
                  ["Type", selected.kind],
                  ["Created At", selected.created_at],
                ].map(([k, v]) => (
                  <div className="kv-row" key={k}>
                    <span className="k">{k}</span>
                    <span className="v mono" style={{ fontSize: 9 }}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Training Spec */}
              <div>
                <div className="section-sep"><span className="s-label">Specs</span></div>
                <div className="kv-row"><span className="k">Symbol Target</span><span className="v">{symbol} ({interval})</span></div>
                <div className="kv-row"><span className="k">Horizon</span><span className="v">{selected.metrics?.horizon ?? "—"} bars</span></div>
                <div className="kv-row"><span className="k">Features used</span><span className="v">{selected.feature_ids?.length || 0} features</span></div>
              </div>

              {/* Aggregate metrics */}
              <div>
                <div className="section-sep"><span className="s-label">Aggregate Metrics</span></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 4 }}>
                  {[
                    ["Accuracy", fmt(selected.metrics?.accuracy_mean), `std: ${fmt(selected.metrics?.accuracy_std, 4)}`],
                    ["IC", fmt(selected.metrics?.information_coefficient_mean, 4), `std: ${fmt(selected.metrics?.information_coefficient_std, 4)}`, (selected.metrics?.information_coefficient_mean ?? 0) > 0 ? "var(--green)" : "var(--red)"],
                    ["ROC AUC", fmt(selected.metrics?.roc_auc_mean), `std: ${fmt(selected.metrics?.roc_auc_std, 4)}`],
                    ["LogLoss", fmt(selected.metrics?.log_loss_mean), `std: ${fmt(selected.metrics?.log_loss_std, 4)}`],
                    ["Trades", selected.n_trades != null ? selected.n_trades : "—", "Total executions"],
                    ["MDD", selected.max_drawdown_pct != null ? `${selected.max_drawdown_pct.toFixed(2)}%` : "—", "Max Drawdown", (selected.max_drawdown_pct ?? 0) < -10 ? "var(--red)" : "var(--text-primary)"],
                  ].map(([k, v, s, col]) => (
                    <div key={k} className="stat-card" style={{ padding: "4px 6px" }}>
                      <div className="s-label">{k}</div>
                      <div className="s-val" style={{ fontSize: 13, color: col || "var(--text-primary)" }}>{v}</div>
                      <div className="s-sub" style={{ fontSize: 8 }}>{s}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Core Actions */}
              <div>
                <div className="section-sep"><span className="s-label">Deploy & Verify</span></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginTop: 4 }}>
                  <button className="btn" style={{ justifyContent: "center" }} onClick={() => runInference(selected.model_id)} disabled={inferLoading}>
                    {inferLoading ? <RefreshCw size={11} className="spin" /> : <Cpu size={11} />}
                    Inference
                  </button>
                  <button className="btn btn-primary" style={{ justifyContent: "center" }} onClick={() => runBacktest(selected.model_id)} disabled={btLoading}>
                    {btLoading ? <RefreshCw size={11} className="spin" /> : <BarChart3 size={11} />}
                    Run Backtest
                  </button>
                </div>
              </div>

              {/* Verification Output */}
              {inferResult && (
                <div style={{ padding: 8, background: "var(--bg-raised)", border: "1px solid var(--border-dim)", borderRadius: 4 }}>
                  <div style={{ fontSize: 9, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Live Inference Predict</div>
                  {inferResult.error ? (
                    <div style={{ color: "var(--red)", fontSize: 10 }}>Error: {inferResult.error}</div>
                  ) : (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span className={`dir-pill ${inferResult.direction === "UP" ? "up" : inferResult.direction === "DOWN" ? "down" : "flat"}`} style={{ fontSize: 11, padding: "2px 8px" }}>
                        {inferResult.direction}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>P(Up): <span className="mono">{((inferResult.probability_up ?? 0.5)*100).toFixed(1)}%</span></span>
                      <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>Conf: <span className="mono">{((inferResult.confidence ?? 0)*100).toFixed(0)}%</span></span>
                    </div>
                  )}
                </div>
              )}

              {btResult && (
                <div style={{ padding: 8, background: "var(--bg-raised)", border: "1px solid var(--border-dim)", borderRadius: 4 }}>
                  <div style={{ fontSize: 9, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Backtest Metrics</div>
                  {btResult.error ? (
                    <div style={{ color: "var(--red)", fontSize: 10 }}>Error: {btResult.error}</div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
                      {[
                        ["Return", pct(btResult.metrics?.total_return_pct), btResult.metrics?.total_return_pct >= 0 ? "var(--green)" : "var(--red)"],
                        ["Sharpe", fmt(btResult.metrics?.sharpe)],
                        ["Max DD", `${fmt(btResult.metrics?.max_drawdown_pct)}%`, "var(--red)"],
                        ["Trades", btResult.metrics?.n_trades],
                      ].map(([k, v, c]) => (
                        <div key={k} className="kv-row" style={{ padding: "1px 0" }}>
                          <span className="k">{k}</span>
                          <span className="v" style={{ color: c || "var(--text-primary)", fontSize: 10 }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Feature IDs list */}
              {selected.feature_ids && (
                <div>
                  <div className="section-sep"><span className="s-label">Feature Set ({selected.feature_ids.length})</span></div>
                  <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid var(--border-dim)", borderRadius: 3, background: "var(--bg-raised)", padding: 4 }}>
                    {selected.feature_ids.map((fid, i) => (
                      <div key={i} className="mono" style={{ fontSize: 9, color: "var(--text-secondary)", padding: "1.5px 4px", borderBottom: "1px solid var(--border-dim)" }}>
                        {fid}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ color: "var(--text-tertiary)", fontSize: 11, textAlign: "center", padding: 24 }}>Select a model to view full profile</div>
          )}
        </div>
      </div>
    </div>
  );
}
